import { useState } from "react";

import { MarkdownContent, TextField } from "~components";
import type { EmailAnswerType } from "~onboarding/utils";
import { translate as t } from "~utils";

type EmailAnswerProps = Omit<EmailAnswerType, "type"> & {
  /**
   * The value of the entered email.
   */
  value?: string;
  /**
   * When entering text, pass the `value` to the parent.
   */
  onChange: (value?: string) => void;
  /**
   * Optional markdown content to display before the email field.
   */
  markdownBefore?: string;
  /**
   * Disables the editability of the answer.
   */
  isReadOnly?: boolean;
};

export function EmailAnswer(props: EmailAnswerProps) {
  const { value, onChange, isReadOnly, label, placeholder, markdownBefore } = props;
  const [isValid, setIsValid] = useState(true);
  const [email, setEmail] = useState(value);

  const handleChange = (value: string) => {
    setEmail(value);
    if (isValidEmail(value)) {
      setIsValid(true);
      onChange(value === "" ? undefined : value.trim());
    } else {
      setIsValid(false);
      onChange(undefined);
    }
  };

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "var(--spacing-lg)" }}>
      {markdownBefore && (
        <MarkdownContent
          data-testid="markdown-before"
          body={markdownBefore}
          css={{ width: "100%" }}
        />
      )}
      <TextField
        type="email"
        value={email}
        onChange={handleChange}
        isDisabled={isReadOnly}
        label={label}
        placeholder={placeholder}
      />
      {!isValid && <div css={{ color: "red" }}>{t("INVALID_EMAIL_ADDRESS")}</div>}
    </div>
  );
}

function isValidEmail(email: string) {
  // This is the same regex used in suber.util.malli.core
  const emailRegex = new RegExp(
    /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/i,
  );
  return emailRegex.test(email);
}
